import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 * Migrated from 3.2.1 custom logic: thumbnail-aware, compatMap local filtering, prefetch cache, re-click handling.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {Map<string, Set<string>> | null} */
  #variantCompatMap = null;

  /** @type {Map<string, string>} */
  #responseCache = new Map();

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.addEventListener('click', this.#handleRadioClick.bind(this));
    this.#resizeObserver.observe(this);
    this.#buildVariantMap();
    this.#hideUnavailableOptions(0);
    this.classList.add('variant-ready');
    requestAnimationFrame(() => this.#prefetchAll());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles clicks on already-checked radio buttons.
   * Re-clicking an already-selected variant triggers gallery update via fetch.
   * @param {Event} event - The click event.
   */
  #handleRadioClick(event) {
    let input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') {
      const label = event.target.closest('label');
      input = label?.querySelector('input[type="radio"]');
    }
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    if (input.dataset.currentChecked !== 'true') return;

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = input.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));
    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const { optionValueId = '', variantId = '', connectedProductUrl = '' } = input.dataset;
    this.fetchUpdatedSection(this.buildRequestUrl(input), {
      morphElementSelector,
      detail: { optionValueId, variantId, connectedProductUrl },
    });
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    const changedFieldsetIndex = Number.parseInt(event.target.dataset.fieldsetIndex ?? '');
    if (!Number.isNaN(changedFieldsetIndex)) {
      this.querySelectorAll('fieldset').forEach((fs, i) => {
        if (i <= changedFieldsetIndex) return;
        fs.querySelectorAll('input').forEach((r) => { r.checked = false; });
      });
    }
    if (changedFieldsetIndex === 0 && this.#variantCompatMap) {
      this.#applyLocalHiding(0, this.#variantCompatMap);
      this.#autoSelectFirstForRemaining(false);
    }

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const { optionValueId = '', variantId = '', connectedProductUrl = '' } = selectedOption.dataset;
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), {
      morphElementSelector,
      detail: { optionValueId, variantId, connectedProductUrl },
    });

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          const newCurrentIndex = checkedIndices[0];
          const newPreviousIndex = checkedIndices[1];

          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {object} [options] - Request options.
   * @param {string} [options.morphElementSelector] - The selector of the element to be morphed.
   * @param {{ optionValueId?: string, variantId?: string, connectedProductUrl?: string }} [options.detail] - Synchronous product select event detail.
   */
  fetchUpdatedSection(requestUrl, { morphElementSelector, detail = {} } = {}) {
    const cached = this.#responseCache.get(requestUrl);
    if (cached) {
      this.#handleCachedResponse(cached, morphElementSelector, detail);
      return;
    }

    const { optionValueId = '', variantId, connectedProductUrl = '' } = detail;
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const deferredEventPromise = ProductSelectEvent.createPromise();
    const selectedOptions = this.getAllSelectedOptions();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: {
          optionValueId,
          variantId,
          connectedProductUrl,
        },
        promise: deferredEventPromise.promise,
      })
    );

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#responseCache.set(requestUrl, responseText);
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const variantPickerJsonScript = html.querySelector(`variant-picker script[type="application/json"]`);
        const textContent = variantPickerJsonScript?.textContent;

        if (!textContent) {
          deferredEventPromise.resolve({
            variant: null,
            detail: {
              html,
              productId: this.dataset.productId ?? '',
              sourceId: this.selectedOptionId,
              resource: null,
            },
          });
          this.#postProcessAfterMorph();
          return;
        }

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        this.#postProcessAfterMorph();

        if (this.selectedOptionId) {
          const variantData = JSON.parse(textContent);

          if (variantData && typeof variantData === 'object') {
            const productViewAttr = variantPickerJsonScript
              ?.closest('[view-event-payload]')
              ?.getAttribute('view-event-payload')
              ?.trim();

            deferredEventPromise.resolve({
              variant: (productViewAttr && JSON.parse(productViewAttr))?.product?.selectedVariant ?? null,
              detail: {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
                sourceId: this.selectedOptionId,
                resource: variantData,
              },
            });

            return;
          }
        }

        deferredEventPromise.resolve({
          variant: null,
          detail: {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
            sourceId: this.selectedOptionId,
            resource: null,
          },
        });
      })
      .catch((error) => {
        deferredEventPromise.reject(error);
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  #handleCachedResponse(cachedText, morphElementSelector, detail) {
    const { optionValueId = '', variantId, connectedProductUrl = '' } = detail;
    const deferredEventPromise = ProductSelectEvent.createPromise();
    const selectedOptions = this.getAllSelectedOptions();
    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: { optionValueId, variantId, connectedProductUrl },
        promise: deferredEventPromise.promise,
      })
    );
    this.#pendingRequestUrl = undefined;
    const html = new DOMParser().parseFromString(cachedText, 'text/html');
    html.querySelector('overflow-list[defer]')?.removeAttribute('defer');
    const variantPickerJsonScript = html.querySelector(`variant-picker script[type="application/json"]`);
    const textContent = variantPickerJsonScript?.textContent;
    if (!textContent) {
      deferredEventPromise.resolve({
        variant: null,
        detail: { html, productId: this.dataset.productId ?? '', sourceId: this.selectedOptionId, resource: null },
      });
      this.#postProcessAfterMorph();
      return;
    }
    let newProduct;
    if (morphElementSelector === 'main') {
      this.updateMain(html);
    } else if (morphElementSelector) {
      this.updateElement(html, morphElementSelector);
    } else {
      const { overflowList } = this.refs;
      const wasSwatchesExpanded =
        overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';
      newProduct = this.updateVariantPicker(html);
      if (wasSwatchesExpanded) {
        const overflowListAfterMorph = overflowList;
        if (overflowListAfterMorph instanceof OverflowList) overflowListAfterMorph.showAll();
      }
    }
    this.#postProcessAfterMorph();
    if (this.selectedOptionId) {
      const variantData = JSON.parse(textContent);
      if (variantData && typeof variantData === 'object') {
        const productViewAttr = variantPickerJsonScript?.closest('[view-event-payload]')?.getAttribute('view-event-payload')?.trim();
        deferredEventPromise.resolve({
          variant: (productViewAttr && JSON.parse(productViewAttr))?.product?.selectedVariant ?? null,
          detail: { html, productId: this.dataset.productId ?? '', newProduct, sourceId: this.selectedOptionId, resource: variantData },
        });
        return;
      }
    }
    deferredEventPromise.resolve({
      variant: null,
      detail: { html, productId: this.dataset.productId ?? '', newProduct, sourceId: this.selectedOptionId, resource: null },
    });
  }

  #postProcessAfterMorph() {
    this.#hideUnavailableOptions(0);
    this.#autoSelectFirstForRemaining(false);
    this.#buildVariantMap();
    if (this.#variantCompatMap) {
      this.#applyLocalHiding(0, this.#variantCompatMap);
    }
  }

  #buildVariantMap() {
    const script = this.querySelector('script[data-option-compat-map]');
    if (!script?.textContent) return;
    let raw;
    try { raw = JSON.parse(script.textContent); } catch { return; }
    if (!raw) return;
    const pairs = raw.split(';').filter(Boolean).map((p) => p.split(','));
    if (!pairs.length) return;
    const map = new Map();
    for (const [op1Id, op2Id] of pairs) {
      if (!map.has(op1Id)) map.set(op1Id, new Set());
      map.get(op1Id).add(op2Id);
    }
    this.#variantCompatMap = map;
  }

  #prefetchAll() {
    let map = this.#variantCompatMap;
    if (!map) {
      this.#buildVariantMap();
      map = this.#variantCompatMap;
    }
    const productUrl = this.dataset.productUrl?.split('?')[0];
    if (!productUrl) return;

    const viewParamValue = getViewParameterValue();
    const viewPart = viewParamValue ? `&view=${viewParamValue}` : '';

    if (!map) {
      const fieldsets = this.querySelectorAll('fieldset');
      const select = this.querySelector('select');
      if (fieldsets.length === 1) {
        fieldsets[0].querySelectorAll('input').forEach((input) => {
          const url = `${productUrl}?option_values=${input.dataset.optionValueId}${viewPart}`;
          fetch(url)
            .then((r) => r.text())
            .then((text) => this.#responseCache.set(url, text))
            .catch(() => {});
        });
      } else if (select) {
        select.querySelectorAll('option').forEach((opt) => {
          if (!opt.dataset.optionValueId) return;
          const url = `${productUrl}?option_values=${opt.dataset.optionValueId}${viewPart}`;
          fetch(url)
            .then((r) => r.text())
            .then((text) => this.#responseCache.set(url, text))
            .catch(() => {});
        });
      }
      return;
    }

    for (const [op1Id] of map) {
      const url = `${productUrl}?option_values=${op1Id}${viewPart}`;
      fetch(url)
        .then((r) => r.text())
        .then((text) => this.#responseCache.set(url, text))
        .catch(() => {});
    }
    for (const [op1Id, op2Ids] of map) {
      for (const op2Id of op2Ids) {
        const url = `${productUrl}?option_values=${op1Id},${op2Id}${viewPart}`;
        fetch(url)
          .then((r) => r.text())
          .then((text) => this.#responseCache.set(url, text))
          .catch(() => {});
      }
    }
  }

  #applyLocalHiding(changedIndex, map) {
    const op1Value = this.querySelector('fieldset:first-child input:checked')?.dataset?.optionValueId;
    if (!op1Value) return;
    const validSet = map.get(op1Value);
    if (!validSet) return;
    this.querySelectorAll('fieldset').forEach((fs, fi) => {
      if (fi <= changedIndex || fi !== changedIndex + 1) return;
      fs.querySelectorAll('label').forEach((label) => {
        const input = label.querySelector('input');
        if (!input) return;
        if (validSet.has(input.dataset.optionValueId)) {
          label.style.removeProperty('display');
        } else {
          label.style.setProperty('display', 'none', 'important');
        }
      });
    });
  }

  #autoSelectFirstForRemaining(dispatchChange = true) {
    const fieldsets = this.querySelectorAll('fieldset');
    if (fieldsets.length <= 1) return;
    fieldsets.forEach((fieldset, index) => {
      if (index === 0) return;
      const checked = fieldset.querySelector('input:checked');
      const checkedHidden = checked && checked.closest('label')?.style.display === 'none';
      if (checked && !checkedHidden) return;
      const target = Array.from(fieldset.querySelectorAll('input')).find((r) => {
        const label = r.closest('label');
        return label && label.style.display !== 'none';
      });
      if (target) {
        fieldset.querySelectorAll('input').forEach((r) => {
          r.checked = false;
          r.dataset.currentChecked = 'false';
        });
        target.checked = true;
        target.dataset.currentChecked = 'true';
        if (dispatchChange) {
          target.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
  }

  #hideUnavailableOptions(skipFieldsetIndex = -1) {
    this.querySelectorAll('fieldset').forEach((fieldset, index) => {
      if (index <= skipFieldsetIndex) return;
      fieldset.querySelectorAll('input[data-option-available="false"]:not([data-variant-id])').forEach((input) => {
        const label = input.closest('label');
        if (label) {
          label.style.setProperty('display', 'none', 'important');
        }
      });
      fieldset.querySelectorAll('select option[data-option-available="false"]:not([data-variant-id])').forEach((option) => {
        option.remove();
      });
    });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets all the selected options.
   * @returns {{name: string, value: string}[]} All the currently selected options.
   */
  getAllSelectedOptions() {
    /** @type {{name: string, value: string}[]} */
    const options = [];

    for (const select of this.querySelectorAll('select')) {
      const selected = select.selectedOptions[0];
      if (selected?.dataset?.optionName) {
        options.push({ name: selected.dataset.optionName, value: selected.value });
      }
    }

    /** @type {NodeListOf<HTMLInputElement>} */
    const checkedInputs = this.querySelectorAll('fieldset input:checked');
    for (const input of checkedInputs) {
      if (input.dataset?.optionName) {
        options.push({ name: input.dataset.optionName, value: input.value });
      }
    }

    return options;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
